<template>
  <component
      :is="tag"
      :class="defineClassForButton"
      v-on="$listeners"
  >
    <slot>
      <ui-icon
          v-if="icon"
          class="ui-button__icon"
          :name="icon"
          :size="innerIconSize"
          :width="iconWidth"
          :height="iconHeight"
      />
      <span
          v-if="title"
          class="ui-button__title">
        {{ title }}
      </span>
    </slot>
  </component>
</template>
<script>
import UiIcon from '../icons/UiIcon.vue'

export default {
  name: 'UiButton',
  components: { UiIcon },
  props: {
    tag: {
      type: String,
      default: 'button'
    },

    title: {
      type: String,
      default: ''
    },

    icon: {
      type: String,
      default: ''
    },

    type: {
      type: String,
      default: 'primary',
      validator (type) {
        return ['primary', 'secondary', 'danger', 'warning', 'success', 'info'].indexOf(type) !== -1
      }
    },

    buttonStyle: {
      type: String,
      default: 'filled',
      validator (type) {
        return ['filled', 'outline', 'link'].indexOf(type) !== -1
      }
    },

    iconHeight: Number,
    iconWidth: Number,

    iconSize: {
      type: Number,
      default: 24
    },

    fullWidth: {
      type: Boolean,
      default: false
    },

    size: {
      type: String,
      default: 'm',
      validator (size) {
        if (!size) return true
        return ['xs', 's', 'm', 'l'].includes(size)
      }
    },

    disabled: {
      type: Boolean,
      default: false
    },

    rounded: {
      type: Boolean,
      default: false
    },

    iconRight: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    defineClassForButton () {
      let mainClass = `ui-button ${this.type} ${this.size} ${this.buttonStyle}`
      if (this.fullWidth) mainClass += ' fill-width'
      if (this.disabled) mainClass += ' disabled'
      if (this.rounded) mainClass += ' round'
      if (this.icon) mainClass += ' has-icon'
      if (this.icon && !this.title && !this.$slots.default) mainClass += ' only-icon'
      if (this.iconRight) mainClass += ' icon-right'
      return mainClass
    },

    innerIconSize () {
      return this.iconWidth || this.iconHeight ? null : this.iconSize
    }
  }
}
</script>

<style lang="scss" scoped>
.ui-button {
  @include flex-center;
  gap: 4px;
  padding-right: 16px;
  padding-left: 16px;
  font-size: 16px;
  font-family: $main-ff;
  color: white;
  line-height: 24px;
  background-color: $white;
  border: 0;
  border-radius: $border-radius-01;
  outline: 0;
  cursor: pointer;
  white-space: nowrap;
  fill: $white;

  @include transition-list([color, background-color, border]);

  &__title {
    text-align: center;
  }

  &.has-icon {
    padding-right: 12px;
    padding-left: 12px;
  }

  &.disabled {
    pointer-events: none;
  }

  &.fill-width {
    width: 100%;
  }

  &.round {
    border-radius: 50%;
  }

  &.icon-right {
    flex-direction: row-reverse;
  }
}

@mixin button ($filledBackgroundColor,
  $filledHoverBackgroundColor, $filledDisabledBackgroundColor,
  $outlineBorderColor, $outlineColor,
  $outlineHoverBackgroundColor, $outlineDisabledColor, $outlineDisabledBorderColor,
  $linkColor, $linkDisabledColor
) {
  &.filled {
    color: $white;
    background-color: $filledBackgroundColor;
    fill: $white;

    &:hover {
      background-color: $filledHoverBackgroundColor;
    }

    &.disabled {
      color: $white;
      background-color: $filledDisabledBackgroundColor;
    }
  }

  &.outline {
    border: 1px solid $outlineBorderColor;
    color: $outlineColor;
    fill: $outlineColor;
    &:hover {
      background-color: $outlineHoverBackgroundColor;
    }

    &.disabled {
      color: $outlineDisabledColor;
      border: 1px solid $outlineDisabledBorderColor;
      fill: $outlineDisabledColor;
    }
  }

  &.link {
    height: unset;
    padding: 0;
    color: $linkColor;
    fill: $linkColor;
    text-underline-offset: 3px;
    background-color: transparent;

    &:hover {
      text-decoration: underline;
    }
    &.disabled {
      color: $linkDisabledColor;
      fill: $linkDisabledColor;
    }
  }
}

.primary {
  @include button($primary, $primary-2, $primary-3, $primary-2, $primary, $primary-4,
      $primary-3, $primary-5, $primary, $primary-3);
}

.warning {
  @include button($orange-1, $orange-2, $orange-3, $orange-3, $orange-1, $orange-4, $orange-3, $orange-4, $orange-1, $orange-3);
}

.success {
  @include button($green-2, #2ECB5A, $green-3, $green-3, $green-1, $green-4, $green-3, $green-4, $green-1, $green-3);
}

.danger {
  @include button($red-1, $red-2, $red-3, $red-3, $red-1, $red-4, $red-3, $red-4, $red-1, $red-3);
}

.secondary {
  @include button($gray-6, $light-05, $gray-7, $gray-5, $gray-1, $gray-7, $gray-4, $gray-5, $gray-2, $gray-4);

  &.filled {
    color: $gray-1;
    fill: $gray-2;
    &.disabled {
      color: $gray-4;
      fill: $gray-4;
    }
  }

  &.outline {
    fill: $primary;

    &.disabled {
      fill: $primary-3;
    }
  }
}

.info {
  @include button($blue-1, $blue-2, $blue-3, $blue-2, $blue-1, $blue-4,
    $blue-3, $blue-5, $blue-1, $blue-3);
}

.l {
  height: 48px;
  padding-top: 12px;
  padding-bottom: 12px;

  &.only-icon {
    width: 48px;
    padding: 12px;
  }
}

.m {
  height: 40px;
  padding-top: 8px;
  padding-bottom: 8px;

  &.only-icon {
    width: 40px;
    padding: 8px;
  }
}

.s {
  @include font-14;
  height: 32px;
  padding-top: 4px;
  padding-bottom: 4px;

  &.only-icon {
    width: 32px;
    padding: 3px;
  }
}

.xs {
  @include font-12;
  height: 28px;
  padding-top: 2px;
  padding-bottom: 2px;

  &.only-icon {
    width: 28px;
    padding: 2px;
  }
}
</style>
